import {
    BsdfPrincipled,
    Color,
    deserializeMaterialGraph,
    isVec3,
    MaterialConnection,
    MaterialNode,
    TextureFromRevision,
    TextureFromSet,
    TextureSet,
    TextureType,
} from "@cm/material-nodes"
import type {ImageMapInfo, InputMapsInfo, LegacyId, NonNullableDeep, RequiredDeep} from "#material-maps-exporter/core/types"
import {
    isSupportedLinearImageOperatorSequenceNode,
    materialMapsExporterLogger,
    PLATFORM_TEXTURE_TYPE_TO_CHANNEL_LAYOUT,
} from "#material-maps-exporter/core/types"
import type {MapsExporterDataSource, TextureRevisionSnapshot} from "#material-maps-exporter/core/data-source"

type ValidatedTextureRevision = NonNullableDeep<RequiredDeep<TextureRevisionSnapshot>>

export async function gatherMaterialData(
    dataSource: MapsExporterDataSource,
    materialId: {legacyId: LegacyId} | {id: string},
): Promise<{
    mapsInfo: InputMapsInfo
    materialRevisionDetails: {
        id: string
        legacyId: number
    }
}> {
    const mapsInfo: InputMapsInfo = {}

    const isValidTextureRevisionDetails = (x: TextureRevisionSnapshot): x is ValidatedTextureRevision => {
        return (
            x.texture != null &&
            typeof x.texture.type === "string" &&
            typeof x.width === "number" &&
            typeof x.height === "number" &&
            x.dataObject != null &&
            typeof x.dataObject.legacyId === "number" &&
            typeof x.dataObject.width === "number" &&
            typeof x.dataObject.height === "number"
        )
    }

    type ExtendedMapInfoData = {data: Extract<InputMapsInfo[keyof InputMapsInfo]["data"], {type: "DataObject"}>; textureType: TextureType}

    const getExtendedMapInfoDataForTextureFromRevision = async (node: TextureFromRevision): Promise<ExtendedMapInfoData> => {
        const textureRevisionLegacyId = node.parameters.settings.textureRevisionLegacyId
        if (!textureRevisionLegacyId) throw Error(`TextureFromRevision node does not have a textureRevisionLegacyId parameter`)
        const resolvedTextureRevision = await dataSource.resolveTextureRevision(textureRevisionLegacyId)
        const resolvedImageDataObject = await dataSource.resolveDataObject(resolvedTextureRevision.dataObjectId)

        return {
            data: {
                type: "DataObject",
                dataObjectId: resolvedTextureRevision.dataObjectLegacyId,
                width: resolvedImageDataObject.width!,
                height: resolvedImageDataObject.height!,
                physicalWidth: resolvedTextureRevision.widthCm,
                physicalHeight: resolvedTextureRevision.heightCm,
                displacementScale: resolvedTextureRevision.displacementCm,
            },
            textureType: resolvedTextureRevision.textureType,
        }
    }

    const getExtendedMapInfoDataForTextureSetReferencingNodeAndTextureType = async (
        node: TextureSet | TextureFromSet,
        textureType: TextureType,
    ): Promise<ExtendedMapInfoData> => {
        const textureSetRevisionId = node.parameters.settings.textureSetRevisionId
        if (!textureSetRevisionId) throw Error(`Missing textureSetRevisionId parameter on ${node.nodeClass} node`)
        const resolvedTextureSetRevision = await dataSource.resolveTextureSetRevision(textureSetRevisionId)

        const assignmentForTextureType = (() => {
            const assignment = resolvedTextureSetRevision.mapAssignments.find((assignment) => assignment.textureType === textureType)
            if (!assignment) throw Error(`Texture set revision does not have an assignment for texture type ${textureType}`)
            return assignment
        })()
        const resolvedImageDataObject = await dataSource.resolveDataObject(assignmentForTextureType.id)

        return {
            data: {
                type: "DataObject",
                dataObjectId: assignmentForTextureType.legacyId,
                width: resolvedImageDataObject.width!,
                height: resolvedImageDataObject.height!,
                physicalWidth: resolvedTextureSetRevision.widthCm,
                physicalHeight: resolvedTextureSetRevision.heightCm,
                displacementScale: resolvedTextureSetRevision.displacementCm,
            },
            textureType: textureType,
        }
    }

    const getExtendedMapInfoDataForTextureFromSetNode = async (node: TextureFromSet): Promise<ExtendedMapInfoData> => {
        const textureType = node.mapAssignmentTextureType
        if (!textureType) {
            throw Error(`TextureFromSet node does not have a mapAssignmentTextureType parameter`)
        }
        return getExtendedMapInfoDataForTextureSetReferencingNodeAndTextureType(node, textureType)
    }

    const appendMapInfo = (info: {
        type: TextureType
        dataObjectId: number
        width: number
        height: number
        physicalWidth: number
        physicalHeight: number
        displacementScale?: number
        transmissionMin?: number
        transmissionMax?: number
    }) => {
        const textureType = info.type
        if (mapsInfo[textureType] !== undefined) return
        if (PLATFORM_TEXTURE_TYPE_TO_CHANNEL_LAYOUT[textureType] === undefined) return
        mapsInfo[textureType] = {
            data: {
                type: "DataObject",
                dataObjectId: info.dataObjectId,
                width: info.width,
                height: info.height,
                physicalWidth: info.physicalWidth,
                physicalHeight: info.physicalHeight,
                displacementScale: info.displacementScale,
                transmissionMin: info.transmissionMin,
                transmissionMax: info.transmissionMax,
            },
            transformations: [],
        }
    }

    // Phase 0: Fetch all data at once
    const revisionData = await dataSource.fetchMaterialRevision(materialId)
    const materialGraph = deserializeMaterialGraph(revisionData.graphNode)

    //Phase 1.: Try to get as much as possible by traversing the material graph
    {
        type BsdfInput = "alpha" | "anisotropic" | "anisotropicRotation" | "baseColor" | "metallic" | "normal" | "roughness" | "specular" | "tangent"
        const bsdfTextureMap: [BsdfInput, TextureType][] = [
            ["baseColor", TextureType.Diffuse],
            ["metallic", TextureType.Metalness],
            ["specular", TextureType.SpecularStrength],
            ["roughness", TextureType.Roughness],
            ["alpha", TextureType.Mask],
        ]

        const bsdfNodes = materialGraph.nodes.filter((node) => node instanceof BsdfPrincipled)
        if (bsdfNodes.length !== 1) throw Error("Material does not have exactly one Principled BSDF node")
        const bsdfNode = bsdfNodes[0]

        const parameterToShaderNodeRGB = (parameter: unknown): Color => {
            if (parameter instanceof Color) return parameter
            else if (typeof parameter === "number") {
                return new Color({
                    parameters: {
                        inputs: {},
                        settings: {
                            color: {r: parameter, g: parameter, b: parameter},
                        },
                    },
                })
            } else if (isVec3(parameter)) {
                return new Color({
                    parameters: {
                        inputs: {},
                        settings: {
                            color: {r: parameter.x, g: parameter.y, b: parameter.z},
                        },
                    },
                })
            } else throw Error(`Parameter ${parameter} not supported`)
        }

        const collectLinearImageOperatorSequence = (node: unknown) => {
            const linearTransforms: MaterialNode[] = []

            const traverse = (node: unknown) => {
                if (node instanceof MaterialConnection) {
                    traverse(node.source)
                    return
                }
                if (!(node instanceof MaterialNode)) throw Error(`Node is not a MaterialNode: ${node}`)
                if (!isSupportedLinearImageOperatorSequenceNode(node)) throw Error(`Node type ${node.nodeClass} is not supported`)
                linearTransforms.push(node)
                if (node instanceof TextureFromRevision || node instanceof TextureSet || node instanceof TextureFromSet) return
                if (node.parameters.inputs && Object.keys(node.parameters.inputs).length > 0) {
                    if (Object.keys(node.parameters.inputs).length !== 1)
                        throw Error(`Node type ${node.nodeClass} has more than one input, the graph is not linear`)
                    if (!("color" in node.parameters.inputs)) throw Error(`Node type ${node.nodeClass} does not have a 'color' input`)
                    traverse(node.parameters.inputs.color)
                } else if (node instanceof Color) {
                    const colorParameter = node.parameters.settings.color
                    if (colorParameter !== undefined) linearTransforms.push(node)
                    else throw Error(`Final node type ${node.nodeClass} does not have a Color parameter`)
                }
            }

            traverse(node)
            return linearTransforms.reverse()
        }

        const getBsdfLinearImageOperatorSequence = (
            bsdfNode: BsdfPrincipled,
            bsdfInput: keyof BsdfPrincipled["parameters"]["inputs"] & keyof BsdfPrincipled["parameters"]["settings"],
        ) => {
            const inputNode = bsdfNode.parameters.inputs?.[bsdfInput]
            if (inputNode === undefined) {
                const parameter = bsdfNode.parameters.settings?.[bsdfInput]
                if (parameter !== undefined) return [parameterToShaderNodeRGB(parameter)]
                else throw Error(`BSDF node does not have input ${bsdfInput}`)
            } else return collectLinearImageOperatorSequence(inputNode)
        }

        for (const [bsdfInput, textureType] of bsdfTextureMap) {
            // TODO: move into backend
            try {
                const bsdfLinearImageOperatorSequence = getBsdfLinearImageOperatorSequence(bsdfNode, bsdfInput)
                if (bsdfLinearImageOperatorSequence.length === 0) throw Error(`BSDF input ${bsdfInput} does not have a linear image operator sequence`)
                const initialNode = bsdfLinearImageOperatorSequence[0]

                const mapsInfoData = await (async () => {
                    if (initialNode instanceof TextureFromRevision) return (await getExtendedMapInfoDataForTextureFromRevision(initialNode)).data
                    else if (initialNode instanceof TextureSet)
                        return (await getExtendedMapInfoDataForTextureSetReferencingNodeAndTextureType(initialNode, textureType)).data
                    else if (initialNode instanceof TextureFromSet) return (await getExtendedMapInfoDataForTextureFromSetNode(initialNode)).data
                    else if (initialNode instanceof Color) return {type: "ShaderNodeRGB" as const, shaderNodeRGB: initialNode}
                    else throw Error(`BSDF input ${bsdfInput} sequence does not start with a TexImage or ShaderNodeRGB node`)
                })()

                const remainingNodes = bsdfLinearImageOperatorSequence.slice(1)
                if (
                    remainingNodes.find(
                        (node) => node instanceof TextureSet || node instanceof TextureFromSet || node instanceof TextureFromRevision || node instanceof Color,
                    ) !== undefined
                )
                    throw Error(`BSDF input ${bsdfInput} sequence contains a TextureSet, TextureFromSet, TexImage or Color node after the initial node`)

                mapsInfo[textureType] = {
                    data: mapsInfoData,
                    transformations: remainingNodes,
                }
            } catch (error) {
                materialMapsExporterLogger?.debug(`Warning: Could not traverse material graph for slot ${bsdfInput}. Error: ${error}`)
            }
        }
    }

    // TODO: move into backend
    //Phase 2.: Fill potential information from untraversed nodes via texture revisions and texture set revision
    //NOTE: We explicitly ignore textures with MASK type in this step. For the exporter, only the MASK textures which contribute to
    //the alpha input on principled shader are currently relevant. We assume that those have been discovered and correctly
    //accounted for in Phase 1. This way we avoid accidentally interpreting a dirt map (also stored on the platform as a MASK texture) as an alpha map.
    {
        const ignoredTextureTypes = [TextureType.Mask]
        const allNodes = materialGraph.nodes
        const textureFromRevisionNodes = allNodes.filter((node) => node instanceof TextureFromRevision)
        const mapInfoForTextureFromRevisionNodes = await Promise.all(
            textureFromRevisionNodes.map(async (node) => getExtendedMapInfoDataForTextureFromRevision(node)),
        )
        const mapInfoForTextureSetNodes = (
            await Promise.all(
                allNodes
                    .filter((node) => node instanceof TextureSet)
                    .map(async (node) => {
                        const textureSetRevisionId = node.parameters.settings.textureSetRevisionId
                        if (!textureSetRevisionId) throw Error(`Missing textureSetRevisionId parameter on TextureSet node`)
                        const resolvedTextureSetRevision = await dataSource.resolveTextureSetRevision(textureSetRevisionId)
                        return Promise.all(
                            resolvedTextureSetRevision.mapAssignments.map(async (assignment) => {
                                const partialMapsInfo = await getExtendedMapInfoDataForTextureSetReferencingNodeAndTextureType(node, assignment.textureType)
                                return {
                                    data: {
                                        ...partialMapsInfo.data,
                                        transmissionMax: node.parameters.settings.transmissionMax,
                                        transmissionMin: node.parameters.settings.transmissionMin,
                                    },
                                    textureType: assignment.textureType,
                                }
                            }),
                        )
                    }),
            )
        ).flat()
        const mapInfoForTextureFromSetNodes = await Promise.all(
            allNodes.filter((node) => node instanceof TextureFromSet).map(getExtendedMapInfoDataForTextureFromSetNode),
        )
        ;[...mapInfoForTextureFromRevisionNodes, ...mapInfoForTextureSetNodes, ...mapInfoForTextureFromSetNodes]
            .filter((info) => !ignoredTextureTypes.includes(info.textureType))
            .forEach((info) =>
                appendMapInfo({
                    ...info.data,
                    type: info.textureType,
                }),
            )
        //Heuristic to grab the displacement texture from the normal map if it is not explicitly provided in the material revision
        await (async () => {
            if (mapsInfo[TextureType.Displacement] !== undefined) return
            const normalMapTextureFromRevisionNodeIdx = mapInfoForTextureFromRevisionNodes.findIndex((info) => info.textureType === TextureType.Normal)
            if (normalMapTextureFromRevisionNodeIdx === -1) return
            const normalMapTextureFromRevisionNode = textureFromRevisionNodes[normalMapTextureFromRevisionNodeIdx]
            const normalMapTextureRevisionLegacyId = normalMapTextureFromRevisionNode.parameters.settings.textureRevisionLegacyId
            if (!normalMapTextureRevisionLegacyId) return
            const normalToDisplacementData = await dataSource.fetchNormalToDisplacementRevision(normalMapTextureRevisionLegacyId)
            const displacementTextures =
                normalToDisplacementData.texture?.textureSet.textures.filter((texture) => texture.type === TextureType.Displacement) ?? []
            if (displacementTextures.length !== 1) return
            const normalMapInfo = mapInfoForTextureFromRevisionNodes[normalMapTextureFromRevisionNodeIdx].data
            const matchingDisplacementTextureRevision = displacementTextures[0].revisions.find((displacementRevision) => {
                return (
                    displacementRevision.createdByVersion === normalToDisplacementData.createdByVersion &&
                    displacementRevision.width === normalMapInfo.physicalWidth &&
                    displacementRevision.height === normalMapInfo.physicalHeight
                )
            })
            if (matchingDisplacementTextureRevision && isValidTextureRevisionDetails(matchingDisplacementTextureRevision))
                appendMapInfo({
                    type: matchingDisplacementTextureRevision.texture.type as TextureType,
                    dataObjectId: matchingDisplacementTextureRevision.dataObject.legacyId,
                    width: matchingDisplacementTextureRevision.dataObject.width,
                    height: matchingDisplacementTextureRevision.dataObject.height,
                    physicalWidth: matchingDisplacementTextureRevision.width,
                    physicalHeight: matchingDisplacementTextureRevision.height,
                    displacementScale: matchingDisplacementTextureRevision.displacement ?? undefined,
                })
        })()
    }

    return {mapsInfo, materialRevisionDetails: {id: revisionData.id, legacyId: revisionData.legacyId}}
}

export function getAveragePhysicalSize(imageMapsInfo: ImageMapInfo[]): {width: number; height: number; inhomogeneousPhysicalSizesDetected: boolean} {
    const retValue = imageMapsInfo.reduce<{width: number; height: number}>(
        (acc, cur) => ({
            width: acc.width + cur.physicalWidth / Object.keys(imageMapsInfo).length,
            height: acc.height + cur.physicalHeight / Object.keys(imageMapsInfo).length,
        }),
        {width: 0, height: 0},
    )

    let inhomogeneousPhysicalSizesDetected = false
    for (const imageMapInfo of imageMapsInfo) {
        if (Math.abs(imageMapInfo.physicalWidth - retValue.width) > 0.1 || Math.abs(imageMapInfo.physicalHeight - retValue.height) > 0.1) {
            materialMapsExporterLogger?.debug(`Warning: Inhomogeneous physical sizes detected for export, DPI information will not be set`)
            inhomogeneousPhysicalSizesDetected = true
        }
    }

    return {...retValue, inhomogeneousPhysicalSizesDetected}
}
